/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { changeRequestTypesApiService } from '../../../services/api/api.changerequesttypes'
import { useRcmTableData } from '../../../hooks/useRcmTableData'

interface Template {
  value: string
  label: string
}

interface FilterFormValues {
  templates: Template[]
}

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()
  const persistenceKey = 'settingsWorkflowTemplatesFilter'

  const getDefaultFilter = (): FilterFormValues => ({
    templates: []
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(getDefaultFilter())

  useEffect(() => {
    // Load persisted filter on mount
    const persistedFilter = context.service.persist.getGlobal<FilterFormValues>(persistenceKey)
    if (persistedFilter && persistedFilter.templates && persistedFilter.templates.length > 0) {
      setFormValues(persistedFilter)
      tableData.applyFilter(persistedFilter)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setGlobal(persistenceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const isFilterDefault = (filter: FilterFormValues = formValues) => {
    const defaultFilter = getDefaultFilter()
    return JSON.stringify(filter) === JSON.stringify(defaultFilter)
  }

  const updateField = (field: keyof FilterFormValues, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-sm-6 col-lg-4">
              <div className="form-group" id="c221f13a-3305-426b-9b1e-4b64966cf5f7">
                <label className="control-label">{translate('WF_TEMPLATE_LABEL')}</label>
                <InfiniteScrollSelect
                  placeholder={translate('SELECT_WF_TEMPLATES_PLACEHOLDER...')}
                  name="templates"
                  value={formValues.templates}
                  asyncLoadFn={async (search: string, page: number) => {
                    try {
                      const response: any = await changeRequestTypesApiService.getOptionsForAsyncPicker(search, page)
                      const options =
                        response.elements?.map((item: any) => ({
                          value: String(item.id),
                          label: item.name
                        })) || []
                      const hasMore = response.elements?.length > 0 && (page + 1) * 25 < response.totalElements
                      return {
                        options,
                        hasMore
                      }
                    } catch (error) {
                      console.error('Error loading workflow template options:', error)
                      return {
                        options: [],
                        hasMore: false
                      }
                    }
                  }}
                  onChange={(option: any) => {
                    updateField('templates', option ? option : [])
                  }}
                  multi={true}
                />
              </div>
            </div>
          </div>

          <div>
            <button
              type="button"
              className="btn btn-primary"
              id="4709155c-2770-4ea3-aa3d-b6eae6da24bc"
              onClick={handleApplyFilter}
            >
              {translate('BTN_FILTER')}
            </button>
            <button
              type="button"
              className="btn btn-chita btn-link"
              id="9e69c4d2-9805-42fe-ab6b-3b74a2a6d22c"
              onClick={handleClearFilter}
              disabled={isFilterDefault()}
            >
              {translate('BTN_CLEAR_FILTER')}
            </button>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
